import { BodyText } from "../../../shared/ui";
import { KybRequirement } from "../types";

export type OwnershipTab = "ownership" | "other-details";
export type FlagTargetKind = "document" | "information";

export type ChecklistItem = {
  id: string;
  label: string;
  value: string;
  requirement: KybRequirement;
  person?: {
    targetId?: string;
    type: "director" | "ubo";
  };
  approved?: boolean;
  flagged?: boolean;
};

export type RequirementCounts = {
  pending: number;
  approved: number;
  rejected: number;
};

export const approvedActionColor = "#1DCB1C";

export const ownershipTabs: Array<{ label: string; value: OwnershipTab }> = [
  { label: "Ownership", value: "ownership" },
  { label: "Other details", value: "other-details" },
];

const statChipClassName = "inline-flex items-center gap-1.5";

export const MetricPill = ({
  value,
  icon,
  valueClassName,
  iconClassName,
}: {
  value: number;
  icon: React.ReactNode;
  valueClassName?: string;
  iconClassName?: string;
}) => (
  <span className={statChipClassName}>
    <span
      className={`aku-body-small-regular ${valueClassName ?? "text-[#2C2F44]"}`}
    >
      {value}
    </span>
    <span className={iconClassName}>{icon}</span>
  </span>
);

export const FieldBlock = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex min-w-0 flex-col gap-2">
    <BodyText className="aku-body-small-regular text-[#6F739B]">
      {label}
    </BodyText>
    <div className="aku-body-small-regular text-[#161827]">{value}</div>
  </div>
);

export const SectionTabButton = ({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={
      active
        ? "rounded-[4px] bg-white w-[50%] px-4 py-2 shadow-[0_2px_8px_rgba(15,20,40,0.08)] aku-body-small-medium text-[#161827]"
        : "rounded-[4px] px-4 py-2 w-[50%] aku-body-small-regular text-[#6F739B]"
    }
  >
    {children}
  </button>
);

export const ActionIconButton = ({
  children,
  disabled,
  onClick,
  title,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  title?: string;
}) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className="grid h-9 w-9 place-items-center rounded-full text-[#2B2F49] hover:bg-[#F3F4FF] disabled:cursor-not-allowed disabled:hover:bg-transparent"
  >
    {children}
  </button>
);

export const ChecklistDecisionButton = ({
  active,
  children,
  disabled,
  tone,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  disabled?: boolean;
  tone: "yes" | "no";
  onClick: () => void;
}) => {
  const activeClassName =
    tone === "yes"
      ? "border-[#1DCB1C] bg-[#ECFFEC] text-[#118D17]"
      : "border-[#FFC8C8] bg-[#FFF1F1] text-[#C61616]";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`rounded-[8px] border px-4 py-1 aku-body-small-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        active
          ? activeClassName
          : "border-transparent bg-[#F7F8FD] text-[#161827] hover:bg-[#EEF1FB]"
      }`}
    >
      {children}
    </button>
  );
};

export const isImageDocument = (url?: string) => {
  if (!url) return false;
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url);
};

export const isPdfDocument = (url?: string) => {
  if (!url) return false;
  return /\.pdf(\?|$)/i.test(url);
};

export const getRequirementEndpointKey = (requirement: {
  rawKey?: string;
  key: string;
}) =>
  requirement.rawKey?.trim() ||
  requirement.key.trim().toLowerCase().replace(/\s+/g, "_");

export const getRequirementStatus = (status?: string) =>
  status?.trim().toUpperCase() ?? "";

export const getRequirementStatusLabel = (status?: string) => {
  const normalizedStatus = getRequirementStatus(status);

  if (normalizedStatus === "QUERIED" || normalizedStatus === "REJECTED") {
    return "Flagged";
  }
  if (normalizedStatus === "APPROVED") return "Approved";
  if (normalizedStatus === "PENDING") return "Pending";

  return status || "No status";
};

export const getRequirementStatusBadgeClassName = (status?: string) => {
  const normalizedStatus = getRequirementStatus(status);

  if (normalizedStatus === "QUERIED" || normalizedStatus === "REJECTED") {
    return "bg-[#FFE8B0] text-[#C85600]";
  }

  if (normalizedStatus === "APPROVED") {
    return "bg-[#DDF8DD] text-[#0E9F41]";
  }

  return "bg-[#E5E7F8] text-[#383B56]";
};
